import React, { useEffect, useState } from "react";
import { api, fmtNum } from "@finance/lib/api";
import { exportXlsxMulti } from "@finance/lib/exportCsv";
import WorkingCapital from "@finance/components/WorkingCapital";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { TrendUp, Coins, Package, ChartLineUp, Warning, DownloadSimple } from "@phosphor-icons/react";

const money = (v) => `TSh ${fmtNum(v || 0)}`;

function ExportBar({ children, onExport, testid }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <Button variant="outline" size="sm" data-testid={testid} onClick={onExport}><DownloadSimple size={16} className="mr-1" />Export Excel</Button>
    </div>
  );
}

function DateRange({ range, setRange }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="lx-label">From</span>
      <DateField className="h-9 w-40" data-testid="rep-range-start" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
      <span className="lx-label">To</span>
      <DateField className="h-9 w-40" data-testid="rep-range-end" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
      {(range.start || range.end) && <Button variant="ghost" size="sm" data-testid="rep-range-clear" onClick={() => setRange({ start: "", end: "" })}>Clear</Button>}
      {(range.start || range.end) && <span className="rounded-sm bg-gray-100 px-2 py-1 text-xs text-gray-600" data-testid="rep-range-caption">Showing {range.start || "start"} → {range.end || "today"}</span>}
    </div>
  );
}


function Card({ title, icon, children, testid }) {
  return (
    <div className="rounded-sm border border-gray-200 bg-white p-5" data-testid={testid}>
      <h3 className="mb-4 flex items-center gap-2 font-display text-base font-bold text-gray-900">{icon}{title}</h3>
      {children}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className="rounded-sm border border-gray-200 bg-white p-4">
      <div className="lx-label">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold lx-num ${tone || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function SimpleTable({ cols, rows, testid, empty }) {
  if (!rows?.length) return <div className="py-8 text-center text-sm text-gray-400">{empty || "No data yet."}</div>;
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <thead><tr className="border-b border-gray-200 text-left lx-label">
        {cols.map((c) => <th key={c.key} className={`py-2 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-gray-50" data-testid={`${testid}-row-${i}`}>
            {cols.map((c) => <td key={c.key} className={`py-2 ${c.right ? "text-right lx-num" : "text-gray-700"}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BarBlock({ data, dataKey, nameKey }) {
  if (!data?.length) return null;
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNum(v)} />
          <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 11 }} width={130} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => fmtNum(v)} />
          <Bar dataKey={dataKey} radius={[0, 2, 2, 0]}>{data.slice(0, 8).map((_, i) => <Cell key={i} fill="#1B4383" />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ExecReports() {
  const [rev, setRev] = useState(null);
  const [rp, setRp] = useState(null);
  const [turn, setTurn] = useState(null);
  const [prof, setProf] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });

  const qp = () => {
    const p = new URLSearchParams();
    if (range.start) p.set("start", range.start);
    if (range.end) p.set("end", range.end);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  useEffect(() => {
    api.get(`/exec/reports/revenue${qp()}`).then((r) => setRev(r.data)).catch(() => {});
    api.get(`/exec/reports/profitability${qp()}`).then((r) => setProf(r.data)).catch(() => {});
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/exec/reports/receivables-payables").then((r) => setRp(r.data)).catch(() => {});
    api.get("/exec/reports/inventory-turnover").then((r) => setTurn(r.data)).catch(() => {});
  }, []);

  const suffix = range.start || range.end ? `_${range.start || "start"}_${range.end || "end"}` : "";
  const exportRevenue = () => exportXlsxMulti(`cfo-revenue${suffix}`, [
    { name: "By Client", rows: rev?.by_client, columns: [{ key: "client", label: "Client" }, { key: "qty", label: "Qty" }, { key: "revenue", label: "Revenue" }], total: true },
    { name: "By Product", rows: rev?.by_product, columns: [{ key: "sku", label: "SKU" }, { key: "description", label: "Product" }, { key: "qty", label: "Qty" }, { key: "revenue", label: "Revenue" }], total: true },
  ]);
  const exportRp = () => exportXlsxMulti("cfo-receivables-payables", [
    { name: "Summary", rows: [{ k: "Accounts Receivable (GL 1100)", v: rp?.ar_total }, { k: "DSO (days)", v: rp?.dso_reliable ? rp?.dso : "n/a" }, { k: "Accounts Payable (GL 2000)", v: rp?.ap_total }, { k: "DPO (days)", v: rp?.dpo_reliable ? rp?.dpo : "n/a" }, { k: "Trailing 365d revenue", v: rp?.revenue_365 }, { k: "Trailing 365d purchases", v: rp?.purchases_365 }], columns: [{ key: "k", label: "Metric" }, { key: "v", label: "Value" }] },
    { name: "Receivables by Client", rows: rp?.ar_by_client, columns: [{ key: "party", label: "Client" }, { key: "amount", label: "Outstanding" }], total: true },
    { name: "Payables by Supplier", rows: rp?.ap_by_supplier, columns: [{ key: "party", label: "Supplier" }, { key: "amount", label: "Outstanding" }], total: true },
  ]);
  const exportTurn = () => exportXlsxMulti("cfo-inventory-turnover", [
    { name: "Turnover", rows: turn?.rows, columns: [{ key: "sku", label: "SKU" }, { key: "description", label: "Item" }, { key: "sold_qty", label: "Sold Qty" }, { key: "avg_days_to_sell", label: "Avg Days to Sell" }, { key: "on_hand", label: "On Hand" }, { key: "oldest_days_in_stock", label: "Oldest Days In Stock" }] },
  ]);
  const profCols = (nameCol) => [nameCol, { key: "revenue", label: "Revenue" }, { key: "cost", label: "COGS" }, { key: "gross_profit", label: "Gross Profit" }, { key: "margin_pct", label: "Margin %" }, { key: "estimated", label: "Estimate?", format: (v) => (v ? "EST" : "") }];
  const exportProf = () => exportXlsxMulti(`cfo-profitability${suffix}`, [
    { name: "By Client", rows: prof?.by_client, columns: profCols({ key: "client", label: "Client" }), total: true },
    { name: "By Product", rows: prof?.by_product, columns: profCols({ key: "description", label: "Product" }), total: true },
  ]);

  return (
    <Tabs defaultValue="revenue" data-testid="exec-reports">
      <TabsList data-testid="exec-reports-tabs">
        <TabsTrigger value="revenue" data-testid="rep-tab-revenue">Revenue</TabsTrigger>
        <TabsTrigger value="rp" data-testid="rep-tab-rp">Receivables &amp; Payables</TabsTrigger>
        <TabsTrigger value="cash" data-testid="rep-tab-cash">Cash / Working Capital</TabsTrigger>
        <TabsTrigger value="turnover" data-testid="rep-tab-turnover">Inventory Turnover</TabsTrigger>
        <TabsTrigger value="profit" data-testid="rep-tab-profit">Profitability</TabsTrigger>
      </TabsList>

      {/* ---- Revenue ---- */}
      <TabsContent value="revenue" className="mt-6 space-y-6">
        <ExportBar testid="rep-export-revenue" onExport={exportRevenue}><DateRange range={range} setRange={setRange} /></ExportBar>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi label="Total Revenue (invoiced, ex-VAT)" value={money(rev?.total_revenue)} />
          <Kpi label="Clients" value={rev?.by_client?.length ?? "—"} />
          <Kpi label="Products / SKUs" value={rev?.by_product?.length ?? "—"} />
        </div>
        <Card title="Revenue by Client" icon={<TrendUp size={18} className="text-[#1B4383]" />} testid="rep-revenue-client">
          <BarBlock data={rev?.by_client} dataKey="revenue" nameKey="client" />
          <SimpleTable testid="rep-revenue-client-table" empty="No client invoices yet."
            cols={[{ key: "client", label: "Client" }, { key: "qty", label: "Qty", right: true, render: (r) => fmtNum(r.qty) }, { key: "revenue", label: "Revenue", right: true, render: (r) => money(r.revenue) }]}
            rows={rev?.by_client} />
        </Card>
        <Card title="Revenue by Product / SKU" icon={<Package size={18} className="text-[#1B4383]" />} testid="rep-revenue-product">
          <SimpleTable testid="rep-revenue-product-table" empty="No product revenue yet."
            cols={[{ key: "description", label: "Product", render: (r) => <span>{r.sku ? <span className="mr-1 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{r.sku}</span> : null}{r.description}</span> }, { key: "qty", label: "Qty", right: true, render: (r) => fmtNum(r.qty) }, { key: "revenue", label: "Revenue", right: true, render: (r) => money(r.revenue) }]}
            rows={rev?.by_product} />
        </Card>
      </TabsContent>

      {/* ---- Receivables & Payables ---- */}
      <TabsContent value="rp" className="mt-6 space-y-6">
        <ExportBar testid="rep-export-rp" onExport={exportRp}><span className="text-xs text-gray-400">GL balances as of today</span></ExportBar>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Accounts Receivable (GL)" value={money(rp?.ar_total)} tone="text-emerald-600" />
          <Kpi label="DSO (days sales outstanding)" value={rp?.dso != null && rp?.dso_reliable ? `${rp.dso} d` : "n/a"} />
          <Kpi label="Accounts Payable (GL)" value={money(rp?.ap_total)} tone="text-red-600" />
          <Kpi label="DPO (days payables outstanding)" value={rp?.dpo != null && rp?.dpo_reliable ? `${rp.dpo} d` : "n/a"} />
        </div>
        <div className="text-xs text-gray-400">DSO = AR ÷ (trailing-365d revenue ÷ 365) · {money(rp?.revenue_365)} trailing revenue. DPO = AP ÷ (trailing-365d purchases ÷ 365) · {money(rp?.purchases_365)} trailing purchases. {(rp && ((rp.dso != null && !rp.dso_reliable) || (rp.dpo != null && !rp.dpo_reliable))) ? "Shown as n/a where trailing activity is too small relative to the balance for the ratio to be meaningful." : ""}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Receivables by Client" icon={<TrendUp size={18} className="text-emerald-600" />} testid="rep-ar-client">
            <SimpleTable testid="rep-ar-table" empty="Nothing outstanding."
              cols={[{ key: "party", label: "Client" }, { key: "amount", label: "Outstanding", right: true, render: (r) => money(r.amount) }]}
              rows={rp?.ar_by_client} />
          </Card>
          <Card title="Payables by Supplier" icon={<Coins size={18} className="text-red-600" />} testid="rep-ap-supplier">
            <SimpleTable testid="rep-ap-table" empty="Nothing outstanding."
              cols={[{ key: "party", label: "Supplier" }, { key: "amount", label: "Outstanding", right: true, render: (r) => money(r.amount) }]}
              rows={rp?.ap_by_supplier} />
          </Card>
        </div>
        <div className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500" data-testid="rep-rp-recon-note">
          <strong>AR/AP totals</strong> are the GL control-account balances (1100 / 2000). The party breakdowns below net invoices against payments/allocations and may not tie exactly to the GL totals where opening balances, withholding tax, credit notes or unallocated receipts exist. Use the Statements / Aged Analysis pages for a line-by-line reconciliation.
        </div>
      </TabsContent>

      {/* ---- Working capital ---- */}
      <TabsContent value="cash" className="mt-6">
        <WorkingCapital />
      </TabsContent>

      {/* ---- Inventory turnover ---- */}
      <TabsContent value="turnover" className="mt-6 space-y-6">
        <ExportBar testid="rep-export-turnover" onExport={exportTurn}><span className="text-xs text-gray-400">All-time FIFO receipt→sale</span></ExportBar>
        <Card title="Inventory Turnover — average days from receipt to sale (FIFO)" icon={<ChartLineUp size={18} className="text-[#1B4383]" />} testid="rep-turnover">
          <SimpleTable testid="rep-turnover-table" empty="No stock movements yet."
            cols={[
              { key: "description", label: "Item", render: (r) => <span>{r.sku ? <span className="mr-1 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{r.sku}</span> : null}{r.description}</span> },
              { key: "sold_qty", label: "Sold Qty", right: true, render: (r) => fmtNum(r.sold_qty) },
              { key: "avg_days_to_sell", label: "Avg Days to Sell", right: true, render: (r) => r.avg_days_to_sell != null ? `${r.avg_days_to_sell} d` : "—" },
              { key: "on_hand", label: "On Hand", right: true, render: (r) => fmtNum(r.on_hand) },
              { key: "oldest_days_in_stock", label: "Oldest In Stock", right: true, render: (r) => r.on_hand > 0 ? `${r.oldest_days_in_stock} d` : "—" },
            ]}
            rows={turn?.rows} />
        </Card>
      </TabsContent>

      {/* ---- Profitability ---- */}
      <TabsContent value="profit" className="mt-6 space-y-6">
        <ExportBar testid="rep-export-profit" onExport={exportProf}><DateRange range={range} setRange={setRange} /></ExportBar>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Revenue (ex-VAT)" value={money(prof?.total_revenue)} />
          <Kpi label="COGS (AVCO)" value={money(prof?.total_cost)} />
          <Kpi label="Gross Profit" value={money(prof?.total_gross_profit)} tone="text-emerald-600" />
          <Kpi label="Gross Margin" value={prof?.total_margin_pct != null ? `${prof.total_margin_pct}%` : "—"} />
        </div>
        {prof?.has_estimates && (
          <div className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="rep-profit-estimate-note">
            <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
            <span>{prof.estimate_note}</span>
          </div>
        )}
        <Card title="Gross Margin by Client" icon={<TrendUp size={18} className="text-[#1B4383]" />} testid="rep-profit-client">
          <SimpleTable testid="rep-profit-client-table" empty="No costed sales yet."
            cols={[
              { key: "client", label: "Client", render: (r) => <span>{r.client}{r.estimated ? <span className="ml-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">est.</span> : null}</span> },
              { key: "revenue", label: "Revenue", right: true, render: (r) => money(r.revenue) },
              { key: "cost", label: "COGS", right: true, render: (r) => money(r.cost) },
              { key: "gross_profit", label: "Gross Profit", right: true, render: (r) => money(r.gross_profit) },
              { key: "margin_pct", label: "Margin", right: true, render: (r) => r.margin_pct != null ? `${r.margin_pct}%` : "—" },
            ]}
            rows={prof?.by_client} />
        </Card>
        <Card title="Gross Margin by Product / SKU" icon={<Package size={18} className="text-[#1B4383]" />} testid="rep-profit-product">
          <SimpleTable testid="rep-profit-product-table" empty="No costed sales yet."
            cols={[
              { key: "description", label: "Product", render: (r) => <span>{r.sku ? <span className="mr-1 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{r.sku}</span> : null}{r.description}{r.estimated ? <span className="ml-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">est.</span> : null}</span> },
              { key: "revenue", label: "Revenue", right: true, render: (r) => money(r.revenue) },
              { key: "cost", label: "COGS", right: true, render: (r) => money(r.cost) },
              { key: "gross_profit", label: "Gross Profit", right: true, render: (r) => money(r.gross_profit) },
              { key: "margin_pct", label: "Margin", right: true, render: (r) => r.margin_pct != null ? `${r.margin_pct}%` : "—" },
            ]}
            rows={prof?.by_product} />
        </Card>
      </TabsContent>
    </Tabs>
  );
}
